import React, { useState } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import { VMS_COLORS } from "../constants/theme";
import { vmsApi } from "../services/vmsApi";
import { setVmsSession } from "../utils/auth";

const KatsinaLogo = () => (
  <Box
    w="72px"
    h="72px"
    borderRadius="full"
    display="flex"
    alignItems="center"
    justifyContent="center"
    boxShadow="0 10px 26px rgba(11,122,32,0.18)"
    overflow="hidden"
  >
    <Image
      src="/Katsinalogo.png"
      alt="Katsina State Logo"
      w="100%"
      h="100%"
      objectFit="cover"
    />
  </Box>
);

const VmsLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const triggerDailyMaintenanceIfNeeded = (user) => {
    const role = (user?.role || "").toLowerCase();
    if (!["general_admin", "office_user"].includes(role)) return;
    const identity = user?.email || user?.name || role;
    if (!identity) return;

    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    const todayKey = `${y}-${m}-${d}`;
    const storageKey = `vms_daily_maintenance_last_run:${identity}`;
    const lastRun = localStorage.getItem(storageKey);
    if (lastRun === todayKey) return;

    vmsApi
      .runDailyMaintenance()
      .then(() => {
        localStorage.setItem(storageKey, todayKey);
      })
      .catch(() => {});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Missing fields",
        description: "Please enter your email and password.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const data = await vmsApi.login({ email, password });
      setVmsSession(data?.access_token || "", data?.user || {});
      triggerDailyMaintenanceIfNeeded(data?.user || {});

      toast({
        title: "Login successful",
        description: `Welcome ${data?.user?.name || ""}`,
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      const isFirstTimeLogin = Boolean(data?.user?.firstTimeLogin ?? data?.user?.first_time_login);
      if (isFirstTimeLogin) {
        navigate("/vms/change-password", { replace: true });
        return;
      }
      const target = location.state?.from?.pathname || "/vms/requests";
      navigate(target, { replace: true });
    } catch (err) {
      toast({
        title: "Login failed",
        description: err?.message || "Unable to sign in.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg={VMS_COLORS.bg}
      display="grid"
      placeItems="center"
      p={{ base: 4, md: 8 }}
    >
      <Flex
        w="100%"
        maxW="1080px"
        minH={{ base: "auto", md: "620px" }}
        bg="white"
        borderRadius="24px"
        overflow="hidden"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="0 24px 70px rgba(15,23,42,0.15)"
      >
        <Box
          flex={{ base: "1", md: "0 0 52%" }}
          p={{ base: 8, md: 12 }}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack align="stretch" spacing={5} w="full" maxW="420px">
            <Box display="flex" justifyContent="center">
              <KatsinaLogo />
            </Box>

            <Box>
              <Heading
                fontSize={{ base: "2xl", md: "3xl" }}
                letterSpacing="0.02em"
                color="gray.900"
                mb={1}
                textAlign="center"
              >
                LOGIN
              </Heading>
              <Text textAlign="center" color="gray.500" fontSize="sm">
                Visitor Management System
              </Text>
            </Box>

            <Box as="form" onSubmit={handleSubmit} w="full">
              <VStack spacing={4}>
                <FormControl>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" top="0px" bottom="0px" display="flex" alignItems="center">
                      <Icon as={FiMail} color="gray.500" />
                    </InputLeftElement>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email id"
                      bg="#F3F4F6"
                      borderColor="transparent"
                      h="48px"
                      borderRadius="10px"
                      _focusVisible={{
                        bg: "white",
                        borderColor: VMS_COLORS.primary,
                        boxShadow: `0 0 0 3px rgba(11,122,32,0.15)`,
                      }}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" top="0px" bottom="0px" display="flex" alignItems="center">
                      <Icon as={FiLock} color="gray.500" />
                    </InputLeftElement>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Password"
                      bg="#F3F4F6"
                      borderColor="transparent"
                      h="48px"
                      borderRadius="10px"
                      _focusVisible={{
                        bg: "white",
                        borderColor: VMS_COLORS.primary,
                        boxShadow: `0 0 0 3px rgba(11,122,32,0.15)`,
                      }}
                    />
                    <InputRightElement>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label="Toggle password visibility"
                      >
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  type="submit"
                  w="full"
                  mt={1}
                  h="48px"
                  borderRadius="10px"
                  bg={VMS_COLORS.primary}
                  color="white"
                  fontWeight="700"
                  letterSpacing="0.01em"
                  isLoading={isLoading}
                  boxShadow="0 10px 24px rgba(11,122,32,0.25)"
                  _hover={{ bg: VMS_COLORS.primaryDark, transform: "translateY(-1px)" }}
                  _active={{ transform: "translateY(0)" }}
                  transition="all 0.2s ease"
                >
                  Sign in
                </Button>

                <Button
                  variant="link"
                  size="sm"
                  color="gray.500"
                  fontWeight="600"
                  _hover={{ color: "gray.700", textDecoration: "underline" }}
                  onClick={() => navigate("/vms/forgot-password")}
                >
                  Forgot your password?
                </Button>
              </VStack>
            </Box>
          </VStack>
        </Box>

        <Box
          display={{ base: "none", md: "flex" }}
          flex="1"
          bg={VMS_COLORS.primary}
          color="white"
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <VStack spacing={6} zIndex={1}>
            <Box
              bg="whiteAlpha.200"
              border="1px solid"
              borderColor="whiteAlpha.400"
              borderRadius="22px"
              p={8}
              backdropFilter="blur(6px)"
            >
              <Box w="260px" h="260px" borderBottom="2px solid" borderColor="whiteAlpha.500" position="relative">
                <Box
                  position="absolute"
                  left="52px"
                  bottom="0"
                  w="140px"
                  h="180px"
                  borderTopRadius="70px"
                  bg="whiteAlpha.700"
                >
                  <Box
                    position="absolute"
                    left="34px"
                    top="42px"
                    w="72px"
                    h="96px"
                    bg="white"
                    borderRadius="8px"
                  />
                </Box>
                <Box position="absolute" right="26px" bottom="0">
                  <Box w="18px" h="18px" bg="black" borderRadius="full" ml="8px" />
                  <Box w="34px" h="66px" bg="#22C55E" borderRadius="10px" mt={1} />
                  <Flex>
                    <Box w="12px" h="44px" bg="black" borderRadius="8px" mr="3px" />
                    <Box w="12px" h="44px" bg="black" borderRadius="8px" />
                  </Flex>
                </Box>
              </Box>
            </Box>
          </VStack>
          <Box
            position="absolute"
            w="420px"
            h="420px"
            borderRadius="full"
            bg="rgba(255,255,255,0.08)"
            bottom="-160px"
            right="-100px"
          />
        </Box>
      </Flex>
    </Box>
  );
};

export default VmsLogin;
